<template>
  <div class="device-app-panel">
    <div class="panel-header">
      <span class="panel-title">
        已安装应用<em v-if="appList.length">（{{ appList.length }}）</em>
      </span>
      <el-icon class="header-icon-btn" title="刷新" @click="loadApps">
        <Refresh />
      </el-icon>
    </div>
    <div class="panel-search">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索应用名称/包名"
        :prefix-icon="Search"
        clearable
      />
    </div>
    <div class="panel-actions">
      <el-button type="primary" size="small" @click="openUploadInstall">
        上传安装
      </el-button>
      <el-button size="small" @click="openLibInstall">应用库安装</el-button>
    </div>
    <div class="panel-tip">安装/卸载指令下发后约5-10分钟生效</div>
    <div
      class="app-scroll"
      v-loading="loadingAppList"
      element-loading-background="rgba(0, 0, 0, 0.5)"
    >
      <div v-if="filteredAppList.length" class="app-list">
        <div v-for="app in filteredAppList" :key="app.md5 || app.packageName" class="app-row">
          <img class="app-logo" :src="app.logo" alt="" />
          <div class="app-info">
            <div class="app-name" :title="appName(app)">{{ appName(app) }}</div>
            <div class="app-package" :title="app.packageName">{{ app.packageName || '-' }}</div>
          </div>
          <el-button type="danger" link size="small" @click="uninstallApp(app)">
            卸载
          </el-button>
        </div>
      </div>
      <div v-else class="empty-tip">
        {{ loadingAppList ? '加载中...' : (searchKeyword ? '未找到匹配应用' : '暂无已安装应用') }}
      </div>
    </div>

    <!-- 上传APK安装（无userId，仅推送设备安装，不记录云盘） -->
    <installPackage ref="installPackageRef" @freshList="loadApps" />
    <!-- 应用库选择安装 -->
    <applicationOperations ref="applicationOperationsRef" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Search } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import request from "@/utils/request.ts";
import comCueBox from "@/components/comCue/comCue";
import installPackage from "./installPackage.vue";
import applicationOperations from "@/components/applicationOperations/index.vue";
import API_CONFIG from "../api.json";

const props = defineProps({
  deviceCode: {
    type: String,
    required: true,
  },
});

const loadingAppList = ref(false);
const searchKeyword = ref("");
const appList = ref([]);
const installPackageRef = ref(null);
const applicationOperationsRef = ref(null);

const appName = (app) => app.name || app.fileName || "-";

// 本地搜索过滤
const filteredAppList = computed(() => {
  if (!searchKeyword.value || !searchKeyword.value.trim()) {
    return appList.value;
  }
  const keyword = searchKeyword.value.toLowerCase().trim();
  return appList.value.filter((app) => {
    const name = (appName(app) || "").toLowerCase();
    const packageName = (app.packageName || "").toLowerCase();
    return name.includes(keyword) || packageName.includes(keyword);
  });
});

// 查询当前设备已安装应用
const loadApps = () => {
  loadingAppList.value = true;
  request
    .post(API_CONFIG.batchGetInstallAppList.url, {
      ctrAliaList: [props.deviceCode],
    })
    .then((res) => {
      if (res.code === 0 && res.data && Array.isArray(res.data)) {
        appList.value = res.data;
      } else {
        appList.value = [];
      }
    })
    .catch((e) => {
      ElMessage.error("获取已安装应用失败：" + e.message);
      appList.value = [];
    })
    .finally(() => {
      loadingAppList.value = false;
    });
};

// 卸载单个应用
const uninstallApp = (app) => {
  comCueBox(`确认卸载应用「${appName(app)}」？`).then(() => {
    request
      .post(API_CONFIG.uninstallApps.url, {
        ctrAliaList: [props.deviceCode],
        packageNames: [app.packageName],
      })
      .then((res) => {
        if (res.code == 0) {
          ElMessage.success("卸载指令已下发，请5-10分钟后查看结果");
          loadApps();
        } else {
          ElMessage.error(res.msg || "卸载失败");
        }
      })
      .catch((e) => {
        ElMessage.error("卸载失败：" + e.message);
      });
  });
};

// 上传APK安装
const openUploadInstall = () => {
  installPackageRef.value.open([{ code: props.deviceCode }]);
};

// 应用库选择安装
const openLibInstall = () => {
  applicationOperationsRef.value.open(
    [{ cloudMachineNumber: props.deviceCode }],
    "install"
  );
};

onMounted(() => {
  loadApps();
});
</script>

<style scoped lang="scss">
.device-app-panel {
  width: 380px;
  flex-shrink: 0;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #1d1d1f;
  border-left: 1px solid #333;
  color: #fff;
  padding: 12px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  .panel-title {
    font-size: 14px;
    font-weight: 600;

    em {
      font-style: normal;
      color: #999;
      font-weight: 400;
      margin-left: 2px;
    }
  }

  .header-icon-btn {
    cursor: pointer;
    color: #bbb;
    font-size: 16px;

    &:hover {
      color: #fff;
    }
  }
}

.panel-search {
  margin-bottom: 10px;

  :deep(.el-input__wrapper) {
    background: #2a2a2c;
    box-shadow: 0 0 0 1px #3d3d3f inset;
  }

  :deep(.el-input__inner) {
    color: #ddd;
  }
}

.panel-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.panel-tip {
  font-size: 12px;
  color: #888;
  margin-bottom: 8px;
}

.app-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.app-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 6px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .app-logo {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    border-radius: 8px;
    object-fit: cover;
  }

  .app-info {
    flex: 1;
    min-width: 0;

    .app-name {
      font-size: 13px;
      color: #eee;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .app-package {
      font-size: 11px;
      color: #888;
      margin-top: 2px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.empty-tip {
  padding: 30px 0;
  text-align: center;
  color: #888;
  font-size: 13px;
}
</style>
